import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { autoTable, RowInput } from 'jspdf-autotable';
import { SUMMARY } from 'src/app/components/reports/report-popups/groups/on-road-operation/delivery-manifest/delivery-manifest.constants';
import { PickupManifestGroup } from 'src/app/components/reports/report-popups/groups/on-road-operation/pickup-manifest/pickup-manifest.constants';
import { GetPickupManifest } from '../../models/reports/reports.model';

export type DeliveryManifestItem = Record<string, any>;

export interface DeliveryManifestGroupItem {
  deliveryManifest: DeliveryManifestItem[];
  needsCompletion?: boolean;
}

export interface DeliveryManifestReport {
  response: {
    deliveryManifest: DeliveryManifestItem[];
    [k: string]: any; // for summary fields like totals
  };
}

@Injectable({ providedIn: 'root' })
export class PdfExportService {
  HEADERS = [
    'STOP',
    'CUSTOMER',
    'STREET ADDRESS',
    'CITY',
    'PROVINCE',
    'POSTAL CODE',
    'TRACKNO',
    'SHIPPER',
    'AMT DUE',
    'DENSITY',
    'COMPLETED',
    'EDW',
    'SCANCODE',
    'DR LOCATION',
    'INFO NOTICE',
    'AMT COLLECTED',
    'SIG CLARIFY',
    'DUPLICATE',
    'COMMIT/MET',
  ];

  async exportDeliveryManifestPdfAutoTable(
    deliveryManifestReport: DeliveryManifestReport,
    deliveryManifestGroup: DeliveryManifestGroupItem[],
    reportTitle: string,
    reportFileName: string,
    headerRouteLabel: string,
    headerUserLabel: string,
    headerFromDate: string,
    headerToDate: string,
    isAllOptionSelected: boolean,
    orientation: 'p' | 'l' = 'l',
    format: 'letter' | 'a4' = 'letter',
  ): Promise<void> {
    const suppressHeaderTitle = false;
    const doc = new jsPDF({ orientation, format, unit: 'pt' });
    const logo = await this.loadImageAsDataUrl('assets/UPS_Shield_Black.png');

    // ---- Uniform header spacing controls ----
    const HEADER_FONT_SIZE = 8; // same font size for main + sub headers
    const HEADER_CELL_PADDING = 5; // identical padding for header cells

    // Reserve enough vertical room across ALL pages so rows never collide with header/subheading
    const CONTENT_TOP_MARGIN = 56; // adjust to taste; must be >= tallest header block on page 1
    const MARGIN = { left: 28, right: 28, top: CONTENT_TOP_MARGIN, bottom: 40 };
    const START_Y = MARGIN.top + 15;
    const pageW = (doc.internal.pageSize as any).getWidth();
    const pageH = (doc.internal.pageSize as any).getHeight();

    // Header location
    const location =
      deliveryManifestReport.response?.deliveryManifest?.[0]?.location ?? '';

    // Local header labels (main + sub) for the 6-column layout
    const HEADERS_TOP = [
      'STOP',
      'CUSTOMER',
      'STREET ADDRESS',
      '',
      'CITY',
      'PROV',
      'POSTAL',
    ];

    const HEADERS_SUB = [
      'TRACKNO\nSCANCODE', // under STOP
      'DR LOCATION', // under CUSTOMER
      'SHIPPER\nINFO NOTICE', // under STREET ADDRESS
      'AMT DUE\nAMT COL',
      'DENSITY\nSIG CLARIFY', // under CITY
      'COMPLETED\nDUP', // under PROVINCE
      'EDW\nCOMMIT/MET', // under POSTAL CODE
    ];

    // Fixed Y positions for drawing subheading and (only on page 1) logo/title
    const LOGO_Y = 22;
    const LOGO_W = 32,
      LOGO_H = 32;

    const TITLE_Y = 38; // "Delivery Manifest" (page 1 only)
    const SUBHEAD_Y_FIRST = 65; // subheading on page 1
    const SUBHEAD_Y_OTHER = 44; // subheading on subsequent pages

    // Header/footer painter for every page
    const drawHeaderFooter = (pageNumber: number) => {
      // --- PAGE HEADER ---
      if (!suppressHeaderTitle) {
        if (pageNumber === 1) {
          // Logo + Title (first page only)
          if (logo)
            doc.addImage(logo, 'PNG', MARGIN.left, LOGO_Y, LOGO_W, LOGO_H);

          doc.setFont('helvetica', 'normal').setFontSize(18);
          doc.text(reportTitle, MARGIN.left + LOGO_W + 8, TITLE_Y);

          // Subheading (first page)
          doc.setFont('helvetica', 'normal').setFontSize(11);
          doc.text(
            `Location: ${location}  Route Id: ${headerRouteLabel}  User Id: ${headerUserLabel}  ${headerFromDate} to ${headerToDate}`,
            MARGIN.left,
            SUBHEAD_Y_FIRST,
          );
        } else {
          // Subheading (all other pages) — consistent top margin
          doc.setFont('helvetica', 'normal').setFontSize(11);
          doc.text(
            `Location: ${location}  Route Id: ${headerRouteLabel}  User Id: ${headerUserLabel}  ${headerFromDate} to ${headerToDate}`,
            MARGIN.left,
            SUBHEAD_Y_OTHER,
          );
        }
      } else {
        doc.setFont('helvetica', 'normal').setFontSize(11);
        doc.text(
          `Location: ${location}  Route Id: ${headerRouteLabel}  User Id: ${headerUserLabel}  ${headerFromDate} to ${headerToDate}`,
          MARGIN.left,
          SUBHEAD_Y_OTHER,
        );
      }

      // --- PAGE FOOTER (every page) ---
      const printed = this.formatPrintedTimestamp(new Date());
      doc.setFontSize(8);
      doc.text(
        `Printed ${printed}  Page ${pageNumber}`,
        MARGIN.left,
        pageH - 16,
      );
    };

    // Helper: build two rows (top + sub) for one record
    const buildTwoRowRecord = (
      rec: DeliveryManifestItem,
      allOption: boolean,
    ): { top: string[]; sub: string[] } => {
      const stopOrRoute = allOption
        ? (rec.routeId ?? '')
        : String(rec.stop ?? '');
      const edw =
        rec.edwStart || rec.edwEnd
          ? `${rec.edwStart ?? ''}-${rec.edwEnd ?? ''}`
          : '';
      const commitMet = `${rec.edwCommit ?? ''} / ${rec.edwMet ?? ''}`;

      return {
        // First line across the 6 columns
        top: [
          String(stopOrRoute), // STOP
          String(rec.customer ?? ''), // CUSTOMER
          String(rec.streetAddress ?? ''), // STREET ADDRESS
          '',
          String(rec.city ?? ''), // CITY
          String(rec.prov ?? ''), // PROVINCE
          String(rec.postal ?? ''), // POSTAL CODE
        ],
        // Second line (stacked sub‑fields per column, using line breaks)
        sub: [
          `${rec.trackno ?? ''}\n${rec.scancode ?? ''}`, // under STOP
          String(rec.drLocation ?? ''), // under CUSTOMER
          `${rec.shipper ?? ''}\n${rec.infoNotice ?? ''}`, // under STREET ADDRESS
          `${rec.amtDue ?? ''}\n${rec.amtCol ?? ''}`, // AMT DUE / AMT COLLECTED
          `${rec.density ?? ''}\n${rec.sigClarify ?? ''}`, // under CITY
          `${rec.completed ?? ''}\n${rec.dup ?? ''}`, // under PROVINCE
          `${edw}\n${commitMet}`, // under POSTAL CODE
        ],
      };
    };

    // Build paired rows (top/sub) for all records
    const bodyRows: RowInput[] = [];

    // Two sets: one for signature rows (colSpan), one for normal end rows
    const endRowsSignature = new Set<number>(); // draw border at column 0
    const endRowsNormal = new Set<number>(); // draw border at last column

    let currentRowIndex = 0; // tracks row index inside the AutoTable body
    const isNullOrEmpty = (s: string | null | undefined) =>
      !s || String(s).trim().length === 0;

    for (const g of deliveryManifestGroup) {
      const groupRecords = g.deliveryManifest;
      const groupCount = groupRecords.length;

      const groupIsIncompleted = groupRecords.every((r) =>
        isNullOrEmpty(r.completed),
      );

      let lastRealRowIndexOfGroup = -1;

      for (let i = 0; i < groupCount; i++) {
        const rec = groupRecords[i];

        // ----------------- TOP ROW -----------------
        const stopOrRoute = isAllOptionSelected
          ? (rec.routeId ?? '')
          : String(rec.stop ?? '');
        const topRow: RowInput = [
          String(stopOrRoute),
          String(rec.customer ?? ''),
          String(rec.streetAddress ?? ''),
          '',
          String(rec.city ?? ''),
          String(rec.prov ?? ''),
          String(rec.postal ?? ''),
        ];

        // ----------------- SUB ROW 1 -----------------
        const edw =
          rec.edwStart || rec.edwEnd
            ? `${rec.edwStart ?? ''}-${rec.edwEnd ?? ''}`
            : '';

        const subRow1: RowInput = [
          String(rec.trackno ?? ''),
          String(rec.drLocation ?? ''),
          String(rec.shipper ?? ''),
          String(rec.amtDue ?? ''),
          String(rec.density ?? ''),
          String(rec.completed ?? ''),
          edw,
        ];

        // ----------------- SUB ROW 2 -----------------
        const commitMet = `${rec.edwCommit ?? ''} / ${rec.edwMet ?? ''}`;

        const subRow2: RowInput = [
          String(rec.scancode ?? ''),
          '',
          String(rec.infoNotice ?? ''),
          String(rec.amtCol ?? ''),
          String(rec.sigClarify ?? ''),
          String(rec.dup ?? ''),
          commitMet,
        ];

        // ------- Page space estimation (prevents splitting row trio) -------
        const estTop = 12,
          estSub1 = 14,
          estSub2 = 14;
        const neededHeight = estTop + estSub1 + estSub2;
        const lastY = (doc as any).lastAutoTable?.finalY ?? MARGIN.top + 15;

        if (lastY + neededHeight > pageH - MARGIN.bottom) {
          bodyRows.push([
            {
              content: '',
              styles: { minCellHeight: 1 },
              pageBreak: 'before',
            } as any,
          ]);
          currentRowIndex++;
        }

        // ---------------- PUSH ALL 3 ROWS ----------------
        bodyRows.push(topRow);
        currentRowIndex++;

        bodyRows.push(subRow1);
        currentRowIndex++;

        bodyRows.push(subRow2);
        currentRowIndex++;

        lastRealRowIndexOfGroup = currentRowIndex - 1; // last row of this record
      }

      // ---------------- END OF GROUP BORDER ----------------
      if (!groupIsIncompleted) {
        endRowsNormal.add(lastRealRowIndexOfGroup);
      } else {
        // INCOMPLETE GROUP: signature block
        const signatureRow: RowInput = [
          {
            content:
              'Name _________________________________   ' +
              'Signature __________________________________   ' +
              'Time _____________',
            colSpan: 7,
            styles: {
              font: 'helvetica',
              fontSize: 9,
              halign: 'left',
              cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
              minCellHeight: 14,
            },
          },
        ];

        bodyRows.push(signatureRow);
        currentRowIndex++;

        endRowsSignature.add(currentRowIndex - 1);
      }
    }

    let headerTopY = 0; // y at the very top edge of header box
    let headerBottomY = 0; // y at the very bottom edge of header box
    const headerLeftX = MARGIN.left;
    const headerRightX = pageW - MARGIN.right;

    // ---- Make line spacing uniform for header/subheader text ----
    // NOTE: jsPDF uses this for text rendering; AutoTable leverages jsPDF text.
    const originalLineFactor = (doc as any).getLineHeightFactor
      ? (doc as any).getLineHeightFactor()
      : 1.15;
    doc.setLineHeightFactor(1.05);

    // One AutoTable: two header rows + paired body rows; plain (no borders/fills)
    autoTable(doc, {
      startY: START_Y,
      head: [HEADERS_TOP, HEADERS_SUB],
      body: bodyRows,
      margin: MARGIN,
      theme: 'plain',
      tableWidth: 'wrap',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
        overflow: 'linebreak',
        halign: 'left',
        valign: 'top',
        lineWidth: 0,
      },
      headStyles: {
        fontSize: HEADER_FONT_SIZE,
        fontStyle: 'bold',
        cellPadding: HEADER_CELL_PADDING,
        textColor: 20,
        lineWidth: 0,
      },
      columnStyles: {
        0: { cellWidth: 90, lineWidth: 0 }, // STOP + TRACKNO/SCANCODE
        1: { cellWidth: 85, lineWidth: 0 }, // CUSTOMER + DR LOCATION
        2: { cellWidth: 90, lineWidth: 0 }, // STREET ADDRESS + SHIPPER + INFO NOTICE
        3: { cellWidth: 65, lineWidth: 0 }, // AMT DUE + AMT COLLECTED
        4: { cellWidth: 85, lineWidth: 0 }, // CITY + DENSITY
        5: { cellWidth: 85, lineWidth: 0 }, // PROVINCE + COMPLETED
        6: { cellWidth: 85, lineWidth: 0 }, // POSTAL CODE + EDW + SIG/DUPE/COMMIT
      },

      // header #2 and body sub-rows (smaller/tighter)
      didParseCell: (data) => {
        if (data.section === 'head' && data.row.index === 1) {
          data.cell.styles.fontSize = 8;
          data.cell.styles.cellPadding = 2;
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.section === 'body' && data.row.index % 2 === 1) {
          data.cell.styles.fontSize = 8; // sub-row smaller
          data.cell.styles.cellPadding = {
            top: 2,
            right: 2,
            bottom: 1,
            left: 2,
          };
        }
      },

      didDrawCell: (data) => {
        const isHead = data.section === 'head';
        const isBody = data.section === 'body';

        // Track header rectangle extents
        if (isHead) {
          const topEdge = data.cell.y; // top of the cell
          const bottomEdge = data.cell.y + data.cell.height;

          // First header row sets the top edge
          if (data.row.index === 0) {
            headerTopY = headerTopY ? Math.min(headerTopY, topEdge) : topEdge;
          }
          // Second header row updates bottom edge
          if (data.row.index === 1) {
            headerBottomY = Math.max(headerBottomY, bottomEdge);
          }

          // Draw the horizontal top border once (when drawing first row, last column)
          if (
            data.row.index === 0 &&
            data.column.index === data.table.columns.length - 1
          ) {
            doc.setDrawColor(0);
            doc.setLineWidth(0.7); // slightly heavier like the screenshot
            doc.line(headerLeftX, topEdge, headerRightX, topEdge);
          }

          // Draw the horizontal bottom border once (after second header row, last column)
          if (
            data.row.index === 1 &&
            data.column.index === data.table.columns.length - 1
          ) {
            doc.setDrawColor(0);
            doc.setLineWidth(0.7);
            doc.line(headerLeftX, bottomEdge, headerRightX, bottomEdge);

            // Draw the left & right vertical borders spanning both header rows
            doc.line(headerLeftX, headerTopY, headerLeftX, bottomEdge);
            doc.line(headerRightX, headerTopY, headerRightX, bottomEdge);
          }
        }

        // --- Border after GROUP only, not after every entry ---
        if (isBody) {
          const lastCol = data.table.columns.length - 1;
          const colSpan = (data.cell as any)?.colSpan ?? 1;

          //  Signature row (colSpan > 1): AutoTable calls didDrawCell only for column 0.
          const isSignatureEndRow = endRowsSignature.has(data.row.index);
          const isNormalEndRow = endRowsNormal.has(data.row.index);

          const shouldDrawNow =
            (isSignatureEndRow && data.column.index === 0) ||
            (isNormalEndRow && data.column.index === lastCol);

          if (shouldDrawNow) {
            const y = data.cell.y + data.cell.height;
            doc.setDrawColor(0);
            doc.setLineWidth(0.7); //      doc.setLineWidth(0.7); // heavier like the screenshot
            doc.line(MARGIN.left, y, pageW - MARGIN.right, y);
          }
        }
      },

      // Per-page header/footer
      didDrawPage: (data) => drawHeaderFooter(data.pageNumber),
    });

    // Restore original line height factor (optional)
    doc.setLineHeightFactor(originalLineFactor);

    // ---------- Helpers ----------
    function getValue(obj: any, path: string) {
      return path
        .split('.')
        .reduce(
          (acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined),
          obj,
        );
    }

    // Build the summary row using exact keys
    const summaryRow: string[] = SUMMARY.DATA_FIELDS.map((key: string) => {
      const v = getValue(deliveryManifestReport.response, key);
      return v === undefined || v === null ? '' : String(v);
    });

    // ---------- Compute startY directly below the main autoTable ----------
    const startY = (doc as any).lastAutoTable?.finalY
      ? (doc as any).lastAutoTable.finalY + 16
      : START_Y + 16;

    // ---------- Separator line (move higher to avoid strike-through) ----------
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    // Move the line farther above the text baseline so it won't overlap the glyphs
    const separatorY = startY - 18;
    doc.line(MARGIN.left, separatorY, pageWidth - MARGIN.right, separatorY);

    // ---------- "Summary Data" + filters line (normal content, not header) ----------
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text('Summary Data', MARGIN.left, startY - 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(0);

    const infoLine = [
      'Location:',
      String(deliveryManifestReport.response.location ?? ''),
      '   Route ID:',
      headerRouteLabel,
      '   User ID:',
      headerUserLabel,
    ].join(' ');

    // Put the info line to the right of "Summary Data"
    doc.text(infoLine, MARGIN.left + 90, startY - 6);

    // Keep track of how many pages existed before rendering summary
    const pagesBefore = doc.getNumberOfPages();

    // ---------- Render the Summary table (plain; no borders; NO didDrawPage) ----------
    autoTable(doc, {
      startY,
      head: [SUMMARY.HEADING],
      body: [summaryRow],
      margin: MARGIN,
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 11,
        cellPadding: 6,
        lineWidth: 0, // no cell borders
      },
      headStyles: {
        fontStyle: 'bold',
        lineWidth: 0, // no header borders
      },
      tableLineWidth: 0, // no outer table border
      tableLineColor: 0,
    });

    // ---------- If summary spilled onto new page(s), add ONLY the footer (NO header) ----------
    const pagesAfter = doc.getNumberOfPages();

    if (pagesAfter > pagesBefore) {
      for (let p = pagesBefore + 1; p <= pagesAfter; p++) {
        // Select the overflow summary page
        (doc as any).setPage?.(p);

        const pageH = doc.internal.pageSize.getHeight();

        const printed = this.formatPrintedTimestamp(new Date());
        doc.setFont('helvetica', 'normal').setFontSize(8);
        doc.setTextColor(0);

        doc.text(`Printed ${printed}  Page ${p}`, MARGIN.left, pageH - 16);

        doc.setDrawColor(0);
        doc.setLineWidth(0.5);
        doc.line(MARGIN.left, pageH - 14, pageWidth - MARGIN.right, pageH - 14);
      }
    }

    doc.save(`${reportFileName}.pdf`);
  }

  async exportPickupManifestPdfAutoTable(
    pickupManifestReport: GetPickupManifest,
    pickupManifestGroup: PickupManifestGroup[],
    reportTitle: string,
    reportFileName: string,
    headerRouteLabel: string, // this.reportLoop (or "ALL")
    headerUserLabel: string, // this.driverUserID (or "ALL")
    headerFromDate: string,
    headerToDate: string,
    isAllOptionSelected: boolean,
    orientation: 'p' | 'l' = 'l',
    format: 'letter' | 'a4' = 'letter',
  ): Promise<void> {
    const doc = new jsPDF({ orientation, format, unit: 'pt' });
    const logo = await this.loadImageAsDataUrl('assets/UPS_Shield_Black.png');

    // ---- Layout tuning (mirrors Delivery manifest exporter) ----
    const HEADER_FONT_SIZE = 11;
    const HEADER_CELL_PADDING = 5;

    const CONTENT_TOP_MARGIN = 56;
    const MARGIN = { left: 28, right: 28, top: CONTENT_TOP_MARGIN, bottom: 40 };
    const START_Y = MARGIN.top + 15;
    const pageW = (doc.internal.pageSize as any).getWidth();
    const pageH = (doc.internal.pageSize as any).getHeight();

    const location =
      pickupManifestReport.response?.pickupManifests?.[0]?.['location'] ??
      pickupManifestReport.response?.location ??
      '';

    // ---- 7 columns (same widths / style as Delivery) ----
    //   TOP headers:
    const HEADERS_TOP = [
      'STOP',
      'CUSTOMER',
      'STREET',
      '',
      'CITY',
      'PROVINCE',
      'POSTAL CODE',
    ];

    //   SUB headers (pickup-specific; edit as needed):
    const HEADERS_SUB = [
      'TRACKNO', // under STOP
      'SHIPPER', // under CUSTOMER
      'VAL', // under STREET ADDRESS
      'COLL', // (column reserved)
      'SCANCODE', // under CITY
      'TIME', // under PROV
      'PRN', // under POSTAL
    ];

    // Header measurements
    const LOGO_Y = 22;
    const LOGO_W = 32;
    const LOGO_H = 32;
    const TITLE_Y = 38;
    const SUBHEAD_Y_FIRST = 65;
    const SUBHEAD_Y_OTHER = 44;

    // --- Per-page header/footer painter (same pattern as Delivery) ---
    const drawHeaderFooter = (pageNumber: number) => {
      // Header
      if (pageNumber === 1) {
        if (logo)
          doc.addImage(logo, 'PNG', MARGIN.left, LOGO_Y, LOGO_W, LOGO_H);
        doc.setFont('helvetica', 'normal').setFontSize(18);
        doc.text(reportTitle, MARGIN.left + LOGO_W + 8, TITLE_Y);

        doc.setFont('helvetica', 'normal').setFontSize(11);
        doc.text(
          `Location: ${location}  Route Id: ${headerRouteLabel}  User Id: ${headerUserLabel}  ${headerFromDate} to ${headerToDate}`,
          MARGIN.left,
          SUBHEAD_Y_FIRST,
        );
      } else {
        doc.setFont('helvetica', 'normal').setFontSize(11);
        doc.text(
          `Location: ${location}  Route Id: ${headerRouteLabel}  User Id: ${headerUserLabel}  ${headerFromDate} to ${headerToDate}`,
          MARGIN.left,
          SUBHEAD_Y_OTHER,
        );
      }

      // Footer
      const printed = this.formatPrintedTimestamp(new Date());
      doc.setFontSize(9);
      doc.text(
        `Printed ${printed}  Page ${pageNumber}`,
        MARGIN.left,
        pageH - 16,
      );
    };

    // ---- Build body rows (two rows per item: top + sub) ----
    const bodyRows: RowInput[] = [];

    // We'll draw a thicker line after each GROUP end
    const endRowsNormal = new Set<number>();
    let currentRowIndex = 0;

    for (const g of pickupManifestGroup) {
      const groupRecords = g.pickupManifests ?? [];
      let lastRealRowIndexInGroup = -1;

      for (const rec of groupRecords) {
        // ---- First line (top row) ----
        const stopOrRoute = isAllOptionSelected
          ? (rec.loop ?? g.stop ?? '')
          : String(rec.stop ?? '');

        const topRow: RowInput = [
          String(stopOrRoute ?? ''),
          String(rec.customer ?? ''),
          String(rec.street ?? ''),
          '', // reserved
          String(rec.city ?? ''),
          String(rec.prov ?? ''),
          String(rec.postalcode ?? ''),
        ];

        // ---- Second line (sub row) ----

        const subRow: RowInput = [
          String(rec.trackno ?? ''), // TRACKNO
          String(rec.shipper ?? ''), // SHIPPER
          String(rec.val ?? ''), // VAL
          String(rec.coll ?? ''), // COLL
          String(rec.scancode ?? ''), // SCANCODE
          String(rec.time ?? ''), // TIME
          String(rec.prn ?? ''), // PRN
        ];

        // ---- Prevent splitting a pair across pages ----
        const estTop = 12;
        const estSub = 14;
        const neededHeight = estTop + estSub;
        const lastY = (doc as any).lastAutoTable?.finalY ?? MARGIN.top + 15;
        if (lastY + neededHeight > pageH - MARGIN.bottom) {
          bodyRows.push([
            {
              content: '',
              styles: { minCellHeight: 1 },
              pageBreak: 'before',
            } as any,
          ]);
          currentRowIndex++;
        }

        // Push the paired rows
        bodyRows.push(topRow);
        currentRowIndex++;

        bodyRows.push(subRow);
        currentRowIndex++;

        lastRealRowIndexInGroup = currentRowIndex - 1;
      }

      // mark end-of-group border location
      if (lastRealRowIndexInGroup >= 0) {
        endRowsNormal.add(lastRealRowIndexInGroup);
      }
    }

    // ---- Header frame calculations for custom lines ----
    let headerTopY = 0;
    let headerBottomY = 0;
    const headerLeftX = MARGIN.left;
    const headerRightX = pageW - MARGIN.right;

    // Uniform line spacing for headers/sub-headers
    const originalLineFactor = (doc as any).getLineHeightFactor
      ? (doc as any).getLineHeightFactor()
      : 1.15;
    doc.setLineHeightFactor(1.05);

    // ---- Main AutoTable ----
    autoTable(doc, {
      startY: START_Y,
      head: [HEADERS_TOP, HEADERS_SUB],
      body: bodyRows,
      margin: MARGIN,
      theme: 'plain',
      tableWidth: 'wrap',
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
        overflow: 'linebreak',
        halign: 'left',
        valign: 'top',
        lineWidth: 0, // no borders by default
      },
      headStyles: {
        fontSize: HEADER_FONT_SIZE,
        fontStyle: 'normal',
        cellPadding: HEADER_CELL_PADDING,
        textColor: 20,
        lineWidth: 0,
      },
      columnStyles: {
        0: { cellWidth: 100, lineWidth: 0 }, // STOP / TRACKNO
        1: { cellWidth: 100, lineWidth: 0 }, // CUSTOMER / SHIPPER
        2: { cellWidth: 100, lineWidth: 0 }, // STREET ADDRESS / VAL
        3: { cellWidth: 100, lineWidth: 0 }, // — / COLL
        4: { cellWidth: 100, lineWidth: 0 }, // CITY/ SCANCODE
        5: { cellWidth: 100, lineWidth: 0 }, // PROV/ TIME
        6: { cellWidth: 100, lineWidth: 0 }, // POSTAL/ PRN
      },

      didParseCell: (data) => {
        // sub header row smaller
        if (data.section === 'head' && data.row.index === 1) {
          data.cell.styles.fontSize = 11;
          data.cell.styles.cellPadding = 2;
          data.cell.styles.fontStyle = 'normal';
        }

        // body sub-row (every 2nd row in body)
        if (data.section === 'body' && data.row.index % 2 === 1) {
          data.cell.styles.fontSize = 8;
          data.cell.styles.cellPadding = {
            top: 0,
            right: 2,
            bottom: 0,
            left: 2,
          };
        }
      },

      didDrawCell: (data) => {
        const isHead = data.section === 'head';
        const isBody = data.section === 'body';

        // Track header bounds and draw header borders (heavy box around two header rows)
        if (isHead) {
          const topEdge = data.cell.y;
          const bottomEdge = data.cell.y + data.cell.height;

          if (data.row.index === 0) {
            headerTopY = headerTopY ? Math.min(headerTopY, topEdge) : topEdge;
          }
          if (data.row.index === 1) {
            headerBottomY = Math.max(headerBottomY, bottomEdge);
          }

          if (
            data.row.index === 0 &&
            data.column.index === data.table.columns.length - 1
          ) {
            doc.setDrawColor(0);
            doc.setLineWidth(0.7);
            doc.line(headerLeftX, topEdge, headerRightX, topEdge);
          }

          if (
            data.row.index === 1 &&
            data.column.index === data.table.columns.length - 1
          ) {
            doc.setDrawColor(0);
            doc.setLineWidth(0.7);
            doc.line(headerLeftX, bottomEdge, headerRightX, bottomEdge);
            // left/right verticals spanning both header rows
            doc.line(headerLeftX, headerTopY, headerLeftX, bottomEdge);
            doc.line(headerRightX, headerTopY, headerRightX, bottomEdge);
          }
        }

        // Border after GROUP only
        if (isBody) {
          const isEndRow = endRowsNormal.has(data.row.index);
          if (isEndRow && data.column.index === data.table.columns.length - 1) {
            const y = data.cell.y + data.cell.height;
            doc.setDrawColor(0);
            doc.setLineWidth(0.7);
            doc.line(MARGIN.left, y, pageW - MARGIN.right, y);
          }
        }
      },

      // Per-page header/footer
      didDrawPage: (data) => drawHeaderFooter(data.pageNumber),
    });

    // Restore line height factor
    doc.setLineHeightFactor(originalLineFactor);

    // ---- Simple Summary (local computation) ----
    const pagesBefore = doc.getNumberOfPages();
    const startY = (doc as any).lastAutoTable?.finalY
      ? (doc as any).lastAutoTable.finalY + 16
      : START_Y + 16;

    const BOX_PAD_TOP = 8;
    const BOX_PAD_BOTTOM = 8;

    const boxLeft = MARGIN.left;
    const boxRight = pageW - MARGIN.right;
    const boxTop = startY - BOX_PAD_TOP;

    // separator line above "Summary Data"
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    const separatorY = startY - 11;
    doc.line(MARGIN.left, separatorY, pageWidth - MARGIN.right, separatorY);

    // "Summary Data"
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Summary', MARGIN.left, startY + 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    const infoLine = [
      'Location:',
      String(location ?? ''),
      '                         Loop:',
      headerRouteLabel,
    ].join(' ');
    doc.text(infoLine, MARGIN.left + 90, startY + 2);

    // Summary table (plain; no borders)
    autoTable(doc, {
      startY,
      head: [['Pickup Stops', 'Pickup Packages', 'Total Collected']],
      body: [
        [
          String(pickupManifestReport.response?.pickupStops),
          String(pickupManifestReport.response?.pickupPackages),
          String(pickupManifestReport.response?.totalCollected),
        ],
      ],
      margin: MARGIN,
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 11,
        cellPadding: 6,
        lineWidth: 0,
      },
      headStyles: {
        fontStyle: 'normal',
        lineWidth: 0,
      },
      tableLineWidth: 0,
      tableLineColor: 0,
    });

    const summaryFinalY = (doc as any).lastAutoTable?.finalY ?? startY + 10;
    const pagesAfter = doc.getNumberOfPages();

    // Helper to draw a box on the current page
    const drawSummaryBox = (topY: number, bottomY: number) => {
      const height = Math.max(12, bottomY - topY);
      doc.setDrawColor(0);
      doc.setLineWidth(0.7);
      doc.rect(boxLeft, topY, boxRight - boxLeft, height);
    };

    // ---- Draw the box around the Summary area ----
    // CASE 1: Summary fit on the same page
    if (pagesAfter === pagesBefore) {
      // Keep the box inside the bottom margin if content runs very low
      const maxBottom = pageH - MARGIN.bottom - 6;
      const boxBottom = Math.min(summaryFinalY + BOX_PAD_BOTTOM, maxBottom);
      drawSummaryBox(boxTop, boxBottom);
    } else {
      // CASE 2: Summary spilled to one or more extra pages
      // Box on the first page: from boxTop down to the bottom margin
      (doc as any).setPage?.(pagesBefore); // first page where summary starts
      const firstPageBottom = pageH - MARGIN.bottom - 6;
      drawSummaryBox(boxTop, firstPageBottom);

      // For any fully-filled middle summary pages: draw a full-height box
      if (pagesAfter > pagesBefore + 1) {
        for (let p = pagesBefore + 1; p <= pagesAfter - 1; p++) {
          (doc as any).setPage?.(p);
          const midTop = MARGIN.top + 6; // just below header area
          const midBottom = pageH - MARGIN.bottom - 6; // just above footer area
          drawSummaryBox(midTop, midBottom);
        }
      }

      // On the last page: draw a box from the top margin to the end of the table (with padding)
      (doc as any).setPage?.(pagesAfter);
      const lastTop = MARGIN.top + 6;
      const lastBottom = Math.min(
        summaryFinalY + BOX_PAD_BOTTOM,
        pageH - MARGIN.bottom - 6,
      );
      drawSummaryBox(lastTop, lastBottom);
    }

    doc.save(`${reportFileName}.pdf`);
  }

  async exportGroupCodClearedPdfAutoTable(
    clearedReport: any,
    reportTitle: string,
    reportFileName: string,
    headerTurnInDate: string,
    orientation: 'p' | 'l' = 'l',
    format: 'letter' | 'a4' = 'letter',
  ): Promise<void> {
    const doc = new jsPDF({ orientation, format, unit: 'pt' });
    const logo = await this.loadImageAsDataUrl('assets/UPS_Shield_Black.png');

    const MARGIN = { top: 60, left: 28, right: 28, bottom: 40 };
    const START_Y = MARGIN.top + 20;

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const location = clearedReport.response?.location ?? '';
    const records = clearedReport.response?.CODCollectedCleared ?? [];

    // -------- Headers --------
    const HEADERS = [
      [
        'DRIVER',
        'LOOP',
        'TRACKNO',
        'CUSTOMER',
        'CITY',
        'EXPECTED',
        'CA/CH/C',
        'D/P',
        'TURNED IN',
        'COMMENTS',
        'COLLECTED/CLEARED DATE',
        'DELIVERY DATE',
        'LOCATION CODE',
      ],
    ];

    // -------- Body --------
    const bodyRows = records.map((r: any) => [
      r.driver ?? '',
      r.loop ?? '',
      r.trackno ?? '',
      r.customer ?? '',
      r.city ?? '',
      r.expected ?? '',
      r['ca/ch/cc'] ?? '',
      r['d/p'] ?? '',
      r.turnedin ?? '',
      r.comments ?? '',
      r.codturnindate ?? '',
      r.date ?? '',
      r.locationcode ?? '',
    ]);

    // -------- Header / Footer --------
    const drawHeaderFooter = (page: number) => {
      if (page === 1 && logo) {
        doc.addImage(logo, 'PNG', MARGIN.left, 20, 32, 32);
        doc.setFontSize(18);
        doc.text(reportTitle, MARGIN.left + 40, 42);
      }

      doc.setFontSize(11);
      doc.text(
        `Location: ${location}   Turn In: ${headerTurnInDate}`,
        MARGIN.left,
        page === 1 ? 70 : 45,
      );

      doc.setFontSize(8);
      doc.text(
        `Printed ${this.formatPrintedTimestamp(new Date())}   Page ${page}`,
        MARGIN.left,
        pageH - 16,
      );
    };

    let headerTopY = 0;
    let headerBottomY = 0;
    const headerLeftX = MARGIN.left;
    const headerRightX = pageW - MARGIN.right;

    // -------- Table --------
    autoTable(doc, {
      startY: START_Y,
      head: HEADERS,
      body: bodyRows,
      margin: MARGIN,
      theme: 'plain',

      tableWidth: 'auto',

      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 4,
        overflow: 'linebreak',
        lineWidth: 0, // no default grid lines
      },

      headStyles: {
        fontStyle: 'bold',
        fontSize: 9,
        lineWidth: 0, // no built‑in borders
      },

      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 35 },
        2: { cellWidth: 95 },
        3: { cellWidth: 70 },
        4: { cellWidth: 80 },
        5: { cellWidth: 60 },
        6: { cellWidth: 45 },
        7: { cellWidth: 28 },
        8: { cellWidth: 45 },
        9: { cellWidth: 60 },
        10: { cellWidth: 65 },
        11: { cellWidth: 55 },
        12: { cellWidth: 62 },
      },

      didDrawCell: (data) => {
        if (data.section === 'head') {
          const top = data.cell.y;
          const bottom = data.cell.y + data.cell.height;

          // capture top / bottom once
          headerTopY = headerTopY ? Math.min(headerTopY, top) : top;
          headerBottomY = Math.max(headerBottomY, bottom);

          // when last column of header is drawn → draw border box
          if (data.column.index === data.table.columns.length - 1) {
            doc.setDrawColor(0);
            doc.setLineWidth(0.7);

            // top
            doc.line(headerLeftX, headerTopY, headerRightX, headerTopY);
            // bottom
            doc.line(headerLeftX, headerBottomY, headerRightX, headerBottomY);
            // left
            doc.line(headerLeftX, headerTopY, headerLeftX, headerBottomY);
            // right
            doc.line(headerRightX, headerTopY, headerRightX, headerBottomY);
          }
        }
      },

      didDrawPage: (data) => {
        headerTopY = 0;
        headerBottomY = 0;

        drawHeaderFooter(data.pageNumber);
      },
    });

    // -------- Totals --------
    const startY = (doc as any).lastAutoTable?.finalY ?? START_Y + 10;
    // ---- HR before totals ----
    doc.setDrawColor(0);
    doc.setLineWidth(0.7);

    // slightly above totals so it doesn't strike text
    const hrY = startY + 6;
    doc.line(MARGIN.left, hrY, pageW - MARGIN.right, hrY);
    autoTable(doc, {
      startY: startY + 14,
      body: [
        [
          'Total Expected',
          clearedReport.response?.TotalExpectedCOD ?? '',
          'Total Turned In',
          clearedReport.response?.TotalTurnedInCOD ?? '',
        ],
      ],
      theme: 'plain',
      styles: {
        fontSize: 11,
        cellPadding: 6,
        lineWidth: 0,
      },
      columnStyles: {
        0: { fontStyle: 'bold', halign: 'right' },
        1: { halign: 'left' },
        2: { fontStyle: 'bold', halign: 'right' },
        3: { halign: 'left' },
      },
    });
    doc.save(`${reportFileName}.pdf`);
  }

  private async loadImageAsDataUrl(src: string): Promise<string | null> {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      await img.decode();
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }

  private formatPrintedTimestamp(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `${days[d.getDay()]} ${months[d.getMonth()]} ${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private getFieldIndex(
    field: string,
    idx: number,
    sep: '/' | '-' = '/',
  ): string {
    return field.split(sep)[idx];
  }
}
